import { useNuxtApp, useRuntimeConfig } from 'nuxt/app';
import { onBeforeUnmount } from 'vue';
import {
  genClickEventHandlers,
  PUSH_CLICK_BANNER,
  PUSH_VIEW_BANNER,
} from '~~/common/plugins/aw-analytics';
import type { BannerInteractionInput } from '~~/common/utils/AnalyticsBannerInteraction';

/**
 * What these helpers read off their first argument. Callers pass either a props
 * object or, from inside the mixin, the component instance itself.
 */
export interface BannerInteractionSource {
  analyticsMeasurable?: boolean;
  analyticsBannerInteraction?: BannerInteractionInput;
}

export function genBannerInteractionClickEventHandlers (source: BannerInteractionSource) {
  try {
    return genClickEventHandlers(() => sendBannerInteractionAnalytics(source));
  } catch (err) {
    const { $logger } = useNuxtApp();
    $logger.error(err);
    return null;
  }
}

export function sendBannerInteractionAnalytics (source: BannerInteractionSource) {
  const {
    $awAnalytics,
    $logger,
  } = useNuxtApp();
  try {
    if (!source.analyticsMeasurable) {
      return;
    }
    $awAnalytics[PUSH_CLICK_BANNER]({ analyticsBannerInteraction: source.analyticsBannerInteraction });
  } catch (err) {
    $logger.error(err);
  }
}

export function sendBannerViewInteraction (source: BannerInteractionSource, payload?: BannerInteractionSource['analyticsBannerInteraction']) {
  const {
    $awAnalytics,
    $logger,
  } = useNuxtApp();
  try {
    if (!source.analyticsMeasurable) {
      return;
    }
    const interaction = payload || source.analyticsBannerInteraction;
    $awAnalytics[PUSH_VIEW_BANNER]({ analyticsBannerInteraction: interaction });
  } catch (err) {
    $logger.error(err);
  }
}

/**
 * Returns a throttled sender. It always returns a callable: the previous version
 * returned undefined from its catch branch while every caller invoked the result
 * unconditionally, which would have thrown a TypeError. Errors are handled inside
 * the returned function instead, where the runtime config is actually read.
 */
export function createThrottledSendBannerViewInteraction () {
  const { $logger } = useNuxtApp();
  let timeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
  onBeforeUnmount(() => {
    clearTimeout(timeoutId);
  });
  return function throttledSendBannerViewInteraction (
    source: BannerInteractionSource,
    payload?: BannerInteractionSource['analyticsBannerInteraction'],
  ) {
    try {
      const { public: { viewPromotionAnalyticsIntervalLength } } = useRuntimeConfig();
      timeoutId = setTimeout(
        () => {
          clearTimeout(timeoutId);
          sendBannerViewInteraction(source, payload);
        },
        Number(viewPromotionAnalyticsIntervalLength) / 4,
      );
    } catch (err) {
      $logger.error(err);
    }
  };
}
